import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SeoService } from '../core/services/seo.service';
import { NavigationComponent } from '../shared/navigation.component';
import { FooterComponent } from '../shared/footer.component';

interface BlogPost {
  slug: string;
  title: string;
  description: string;
  publishDate: string;
  readTime: string;
  category: string;
  image: string;
  featured: boolean;
}

@Component({
  selector: 'app-blogs-list',
  standalone: true,
  imports: [CommonModule, RouterModule, NavigationComponent, FooterComponent],
  template: `
    <app-navigation></app-navigation>
    <div class="blogs-container">
      <!-- Header -->
      <header class="blogs-header">
        <h1>Blog de Pezeta Solutions</h1>
        <p class="blogs-subtitle">
          Artículos sobre desarrollo de software, automatización de procesos y transformación digital para empresas mexicanas.
        </p>
      </header>

      <!-- Featured Blog -->
      <section class="featured-section" *ngIf="featuredBlog">
        <h2><img src="icons/star-solid-full.svg" alt="Destacado" class="section-icon-svg"> Artículo destacado</h2>
        <article class="featured-blog">
          <div class="featured-image">
            <img 
              [src]="featuredBlog.image" 
              [alt]="featuredBlog.title"
              loading="eager"
              [attr.width]="600"
              [attr.height]="400"
            >
          </div>
          <div class="featured-content">
            <span class="blog-category">{{featuredBlog.category}}</span>
            <h3>
              <a [routerLink]="['/blogs', featuredBlog.slug]">{{featuredBlog.title}}</a>
            </h3>
            <p>{{featuredBlog.description}}</p>
            <div class="blog-meta">
              <span class="meta-item"><img src="icons/calendar-solid-full.svg" alt="Fecha" class="meta-icon-svg"> {{formatDate(featuredBlog.publishDate)}}</span>
              <span class="meta-item"><img src="icons/clock-solid-full.svg" alt="Tiempo" class="meta-icon-svg"> {{featuredBlog.readTime}}</span>
            </div>
            <a [routerLink]="['/blogs', featuredBlog.slug]" class="read-more-btn">
              Leer artículo completo
            </a>
          </div>
        </article>
      </section>

      <!-- All Blogs -->
      <section class="blogs-grid-section">
        <h2><img src="icons/book-open-solid-full.svg" alt="Artículos" class="section-icon-svg"> Todos los artículos</h2>
        <div class="blogs-grid">
          <a 
            *ngFor="let blog of blogs; trackBy: trackBySlug" 
            [routerLink]="['/blogs', blog.slug]"
            class="blog-card-link"
          >
            <article class="blog-card">
              <div class="blog-card-image">
                <img 
                  [src]="blog.image" 
                  [alt]="blog.title"
                  loading="lazy"
                  [attr.width]="400"
                  [attr.height]="250"
                >
                <span class="blog-category-tag">{{blog.category}}</span>
              </div>
              <div class="blog-card-content">
                <h3>{{blog.title}}</h3>
                <p>{{blog.description}}</p>
                <div class="blog-card-meta">
                  <span class="meta-item"><img src="icons/calendar-solid-full.svg" alt="Fecha" class="meta-icon-svg"> {{formatDate(blog.publishDate)}}</span>
                  <span class="meta-item"><img src="icons/clock-solid-full.svg" alt="Tiempo" class="meta-icon-svg"> {{blog.readTime}}</span>
                </div>
              </div>
            </article>
          </a>
        </div>
      </section>
    </div>

    <app-footer></app-footer>
  `,
  styleUrls: ['./blogs-list.component.css']
})
export class BlogsListComponent implements OnInit {
  
  blogs: BlogPost[] = [
    {
      slug: 'automatizacion-procesos-administrativos',
      title: 'Cómo la automatización puede ahorrarte horas al día en procesos administrativos',
      description: 'Descubre cómo automatizar tareas repetitivas en tu empresa puede ahorrar tiempo, reducir errores y aumentar la productividad sin grandes inversiones.',
      publishDate: '2025-01-17',
      readTime: '8 min',
      category: 'Automatización',
      image: 'images/automatizacion_procesos.webp',
      featured: true
    },
    {
      slug: 'chatbot-whatsapp-para-vender-mas',
      title: '¿Qué es un chatbot para WhatsApp y cómo puede ayudarte a vender más?',
      description: 'Descubre cómo automatizar WhatsApp con un chatbot puede aumentar tus ventas, mejorar la atención al cliente y ahorrar tiempo en tu empresa.',
      publishDate: '2025-06-21',
      readTime: '7 min',
      category: 'Automatización',
      image: 'images/chatbot-whatsapp-para-vender-mas.webp',
      featured: true
    }
  ];

  featuredBlog: BlogPost | null = null;

  constructor(private seoService: SeoService) {}

  ngOnInit() {
    this.featuredBlog = this.blogs.find(blog => blog.featured) || null;
    this.updateSEO();
  }

  private updateSEO() {
    this.seoService.updateSEOData({
      title: 'Blog de Desarrollo de Software y Automatización | Pezeta Solutions',
      description: 'Artículos sobre desarrollo de software personalizado, automatización de procesos, sistemas ERP y transformación digital para empresas mexicanas.',
      keywords: 'blog desarrollo software, automatización procesos, sistemas ERP, transformación digital México, Pezeta Solutions blog, tecnología empresarial',
      canonical: 'https://chatbot.pezetasolutions.com/blogs',
      ogTitle: 'Blog Pezeta Solutions | Desarrollo de Software y Automatización',
      ogDescription: 'Artículos especializados en desarrollo de software, automatización y tecnología empresarial.',
      ogImage: 'https://chatbot.pezetasolutions.com/images/og-image.webp',
      ogUrl: 'https://chatbot.pezetasolutions.com/blogs',
      twitterTitle: 'Blog Pezeta Solutions | Desarrollo de Software',
      twitterDescription: 'Artículos sobre desarrollo de software y automatización empresarial.',
      twitterImage: 'https://chatbot.pezetasolutions.com/images/og-image.webp',
      structuredData: this.generateBlogListStructuredData()
    });
  }

  private generateBlogListStructuredData() {
    return {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "Blog Pezeta Solutions",
      "description": "Artículos sobre desarrollo de software personalizado, automatización de procesos y transformación digital",
      "url": "https://chatbot.pezetasolutions.com/blogs",
      "publisher": {
        "@type": "Organization",
        "name": "Pezeta Solutions",
        "logo": {
          "@type": "ImageObject",
          "url": "https://chatbot.pezetasolutions.com/images/white-logo.webp"
        }
      },
      "blogPost": this.blogs.map(blog => ({
        "@type": "BlogPosting",
        "headline": blog.title,
        "description": blog.description,
        "url": `https://chatbot.pezetasolutions.com/blogs/${blog.slug}`,
        "datePublished": blog.publishDate,
        "image": `https://chatbot.pezetasolutions.com/${blog.image}`,
        "author": {
          "@type": "Organization",
          "name": "Pezeta Solutions"
        }
      }))
    };
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  trackBySlug(index: number, blog: BlogPost): string {
    return blog.slug;
  }
}